import { useEffect, useMemo, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
// W41.0 E4: the 「更多」 trigger draws from the self-drawn set. It was
// `AlignLeft` (lucide) — a hamburger-ish glyph for a button that opens a module
// menu, which reads as "menu" generically rather than "more modules".
import { Icon } from "@/components/ui/Icon";
import { renderModuleIcon } from "@/components/ui/navIcon";

import { cn } from "@/lib/utils/cn";
import {
  isWithinModule,
  MOBILE_MENU_GROUPS,
  type ModuleManifestEntry,
} from "@/lib/moduleManifests";
import {
  gapDown,
  gapUp,
  POPOVER_PANEL_CLASS,
  POPOVER_ROW_CLASS,
  POPOVER_TILE_CLASS,
  POPOVER_TILE_SELECTED_CLASS,
  usePopoverPlacement,
} from "@/components/system/Popover";

/**
 * `PillNavMenu` — the **fourth slot** of the single nav island, and the popover
 * it owns (W38.1 `ADR-0076`, re-shaped by W42 F26).
 *
 * The trigger used to be a 60px island of its own. It is now a `.nav-item` —
 * the same 48px target, the same `flex: 1 1 0` share, the same icon-above-label
 * stack — sitting as the fourth child of `.nav-pill`. The popover lives with it
 * because the popover is anchored to the trigger, so it needs the trigger's ref
 * for click-outside and `Escape`.
 *
 * The island stays lit in two cases, and they are not the same thing:
 *   - the popover is open (armed), and
 *   - the current route is one of the menu modules (you are inside one).
 * The second is the iOS "More" tab behaviour: descend into a More item and More
 * stays lit, while every pill tab goes dark. Getting this wrong is how a user
 * ends up on `/diaries` with no navigation element indicating where they are.
 */

export interface PillNavMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PillNavMenu({ open, onOpenChange }: PillNavMenuProps) {
  const location = useLocation();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // W52.3 — the shared placement rule, asked for `center` explicitly. Measured,
  // this trigger would say "right": it is the fourth of four equal slots, so its
  // centre sits at ~87% across a 298px pill. Centring the panel **on the bar** is
  // what keeps a 240px panel sitting on the nav rather than hanging off its right
  // end — and the bar is what the panel is anchored to anyway, via
  // `display: contents` on the wrapper (see the comment on that below).
  const placement = usePopoverPlacement(triggerRef, "top", "center");

  const menuPaths = useMemo(
    () => MOBILE_MENU_GROUPS.flatMap((group) => group.items.map((item) => item.to)),
    [],
  );
  // Not `pathname === route`: `/settings` owns three sub-screens and `/pair`
  // owns `/pair/invite`, and the popover row for 設定 lights on all of them
  // via NavLink's prefix matching. An exact test would leave the island dark
  // on a screen its own menu row considers active.
  const insideMenu = menuPaths.some((route) => isWithinModule(location.pathname, route));
  const selected = open || insideMenu;

  // Click outside. Capture-phase mousedown so the menu closes on the same
  // gesture that dismisses a native popover, not one event later.
  useEffect(() => {
    if (!open) return;
    const handler = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      onOpenChange(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onOpenChange]);

  // Escape (HIG + keyboard a11y)
  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onOpenChange]);

  // W45.0 `ADR-0115` §P3 — dismiss on **navigation**, not only on tap.
  //
  // The rows are `NavLink`s, so a tap navigated while `open` stayed `true` in the
  // parent's state and the panel stayed on screen over the page you just landed
  // on. Closing inside the row's own `onClick` fixes the tap, but it misses the
  // other ways a route changes: Android's back gesture, a breadcrumb, a link in
  // the page body. All of them leave an orphan panel.
  //
  // A render-phase close is not an option — `onOpenChange` updates state owned by
  // `PillNav`, and React warns against a parent's state being set while
  // rendering. So this is an effect keyed on the pathname, which is also what
  // makes the "armed" state honest: the panel reflects the route you are on.
  const pathname = location.pathname;
  const lastPathname = useRef(pathname);
  useEffect(() => {
    if (lastPathname.current !== pathname) {
      lastPathname.current = pathname;
      if (open) onOpenChange(false);
    }
  }, [pathname, open, onOpenChange]);

  // `inert` on the collapsed menu.
  //
  // The closed menu is hidden with `opacity-0 pointer-events-none`, and neither
  // of those does what hiding means: they leave every row in the tab order and
  // in the accessibility tree. Measured on the dashboard, a Tab walk of the
  // collapsed menu put 5 focus stops on rows the user cannot see
  // (`nav-diaries-popover`, `nav-albums-popover`, `nav-anniversaries-popover`,
  // `nav-pair-popover`, `nav-settings-popover`), so a keyboard user fell
  // through five invisible controls before reaching any content. The mobile
  // UX sweep could not click those rows either, for the same reason.
  //
  // Set through the ref rather than as `inert={open}`: boolean `inert` is
  // React 19+, and this app is on 18.3, where it warns and does not apply.
  useEffect(() => {
    const menu = menuRef.current;
    if (!menu) return;
    if (open) menu.removeAttribute("inert");
    else menu.setAttribute("inert", "");
  }, [open]);

  return (
    // `display: contents` is load-bearing, not tidiness. The trigger has to be
    // a **direct flex child** of `.nav-pill` so it shares the `flex: 1 1 0` with
    // the three route tabs — that is what makes the bar four equal zones. A
    // `relative shrink-0` wrapper (what this was) takes the trigger out of the
    // pill's flex context, so the other three absorbed all the slack and the
    // menu became a fixed box — the 「四颗 chip 掉在长槽里」 shape F35 rejects.
    //
    // The cost is that the wrapper generates no box, so the popover's
    // `position: absolute` resolves against the `<nav>` instead. That is the
    // right answer anyway: the nav *is* the pill, so centring the popover on it
    // is centring it on the bar, whereas anchoring to the old right edge would
    // push a 240px `min-w` popover off the side of a 298px bar.
    <div className="contents">
      <button
        ref={triggerRef}
        type="button"
        data-testid="pill-menu-trigger"
        data-active={selected ? "true" : undefined}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="更多模組"
        onClick={() => onOpenChange(!open)}
        className={cn(
          // `.nav-item` carries the geometry, the 44pt floor and the fluid share.
          // `nav-item-on` is the same state class the route tabs use, so the
          // fourth slot and the first three cannot drift apart visually — and
          // the fill rides `.nav-slot` inside it, exactly as on the tabs.
          "nav-item",
          "tap-target",
          "transition-[transform,box-shadow,background-color] duration-quick ease-quiet",
          "active:scale-95",
          selected && "nav-item-on",
        )}
      >
        <span className="nav-slot">
          <span className="nav-tile">
            <Icon name="more" size={19} />
          </span>
          {/* `aria-hidden` because the button's own `aria-label="更多模組"` is
              already the accessible name — announcing both would say it twice.
              Same treatment the pill tab labels get. */}
          <span aria-hidden>更多</span>
        </span>
      </button>

      <div
        ref={menuRef}
        role="menu"
        aria-label="更多模組"
        data-testid="pill-menu"
        data-open={open ? "true" : undefined}
        // W52.3 — the panel reports the placement the shared rule chose, which
        // is also what the live probe reads back to prove alignment is computed
        // rather than hardcoded.
        data-align={placement.align}
        data-side={placement.side}
        // W52.4 — the gap follows the **side**, not the file. It is `marginTop` on
        // a panel that opens downward and `marginBottom` on one that opens upward,
        // so the two directions cannot both use the same one and hope. See
        // `gapUp` for why this is an inline style and not a class.
        style={placement.side === "top" ? gapUp() : gapDown()}
        className={cn(
          // W52.3 — the panel, its rows and their tiles now come from
          // `@/components/system/Popover`. This file is where the values were
          // born, so it was already correct; what changed is that it is no
          // longer the only place they exist. Four other popovers were painting
          // their own material, radius, shadow and gap.
          //
          // The history below is kept because it is why the values are what
          // they are: W41 `ADR-0105` repainted the bar from glass to solid
          // `chrome-navy` but left this panel on `glass-thick`, so a light card
          // was dropping out of a dark bar. Radius went 16 → 20 because at 16 the
          // panel read as another pill stacked on the first. And `.pop-row`
          // carries its 44px target on `min-height` with a smaller visual inset
          // — the same separation as `.nav-tile` inside `.nav-item`.
          //
          // `origin-bottom`: this panel opens **upward**, so it grows away from
          // the bar rather than toward it.
          //
          // W52.4 — the edge it hangs from is now the shared rule's answer rather
          // than this file's assumption. It reads `top` here because the nav is at
          // the bottom of the screen and there is 774px of room above the trigger,
          // but the hook is allowed to say otherwise and the panel follows it.
          // Hardcoding `bottom-full` while claiming to use `placement.side` would
          // make `data-side` decorative — a value nothing reads, checked by
          // nothing, which is what the probe printed all round while it was wrong.
          "absolute left-1/2 -translate-x-1/2",
          POPOVER_PANEL_CLASS,
          "min-w-60",
          // Gap is the shared 16px, not a number this file chose — it is the
          // one value that had been measured against the bar sitting directly
          // below it, and it is now the app's, not the nav's.
          /* gap via inline style — see `gapUp` */
          placement.side === "top" ? "bottom-full origin-bottom" : "top-full origin-top",
          "transition-all duration-base ease-quiet",
          open ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none",
        )}
      >
        {MOBILE_MENU_GROUPS.map((group) => (
          <div key={group.label} role="group" aria-label={group.label}>
            {/* The visible label and the group's accessible name are the same
                string: `aria-hidden` here is what stops it being announced
                twice, the wrapper carries the semantics. */}
            <div aria-hidden className="px-3 pt-2 pb-1 text-micro font-semibold tracking-wider text-white/45">
              {group.label}
            </div>
            <div className="flex flex-col gap-0.5">
              {group.items.map((item) => (
                <MenuRow
                  key={item.to}
                  to={item.to}
                  label={item.label}
                  icon={item.icon}
                  onSelect={onOpenChange}
                  testId={`${item.testId}-popover`}
                />
              ))}
            </div>
          </div>
        ))}
        {/* W52.1 D-2 — the 「即將」 rows and the rule that separated them are
            gone. Every row in this panel is now an `<a>` to a route that
            exists; nothing here is a greyed placeholder that cannot be
            pressed. `FutureRow` and the `mx-2 my-1 h-px` divider between it
            and the groups were the whole of the dead space. */}
      </div>
    </div>
  );
}

interface MenuRowProps {
  to: string;
  label: string;
  icon: ModuleManifestEntry["icon"];
  /** W45.0: close the panel when the row is chosen. */
  onSelect: (open: boolean) => void;
  testId: string;
}

function MenuRow({ to, label, icon, onSelect, testId }: MenuRowProps) {
  return (
    <NavLink
      to={to}
      role="menuitem"
      data-testid={testId}
      // The fix for 「點了子菜單的項目,子菜單會停留在畫面」. The route-change
      // effect above also covers this, but closing here is the honest place for
      // it: the panel is *this row's* panel, and the other dismissal triggers
      // (hardware back, a breadcrumb) have nothing to do with a row.
      onClick={() => onSelect(false)}
      // `NavLink`'s **children** render prop, not `className`'s: the icon tile
      // needs `isActive` too, and reading it from two different callbacks is how
      // a row ends up with a lilac label on a plain tile.
      className={({ isActive }) =>
        cn(
          POPOVER_ROW_CLASS,
          // `font-medium` on the resting row and `font-semibold` when selected:
          // the nav's own pairing, kept so a selected row is legible without a
          // second colour.
          "font-medium",
          isActive && "bg-surface-lilac/20 text-white font-semibold pop-row-on",
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* The tile is the same 34px object `.nav-tile` became, and the
              selected one spends the SAME lilac the nav slot does — one selected
              colour in the whole navigation.

              `bg-surface-lilac`, not `bg-lilac` (W51 D-7): the theme names the
              token `surface-lilac`, so `bg-lilac` was never a class and emitted
              no CSS. */}
          <span className={cn(POPOVER_TILE_CLASS, isActive && POPOVER_TILE_SELECTED_CLASS)}>
            {renderModuleIcon(icon, { size: 17 })}
          </span>
          <span className="flex-1">{label}</span>
        </>
      )}
    </NavLink>
  );
}
